import { createReducer, on } from '@ngrx/store';
import {
  additionalEntityDocumentsError,
  allowedDocumentTypesError,
  attributeTypesError,
  cancelDocumentUpload,
  clearUploadingDocuments,
  deleteDocumentError,
  deleteDocumentSuccess,
  deletingDocument,
  documentThumbnailsError,
  documentTypesError,
  entityDocumentsError,
  loadingAdditionalEntityDocuments,
  loadingAllowedDocumentTypes,
  loadingAttributeTypes,
  loadingDocumentThumbnails,
  loadingDocumentTypes,
  loadingEntityDocuments,
  loadingMimeTypes,
  loadingRequiredDocumentTypes,
  mimeTypesError,
  removeAllDocuments,
  removeDocuments,
  requiredDocumentTypesError,
  storeActiveDocument,
  storeAdditionalEntityDocuments,
  storeAllowedDocumentTypes,
  storeAttributeTypes,
  storeDocument,
  storeDocumentDict,
  storeDocumentThumbnails,
  storeDocumentTypes,
  storeEntityDocuments,
  storeMimeTypes,
  storeRequiredDocumentTypes,
  updateDocumentError,
  updateDocumentSuccess,
  updateThreadtoEntityDocument,
  updatingDocument,
  uploadDocumentError,
  uploadDocumentProgress,
  uploadDocumentSuccess,
  uploadingDocument,
  clearSingleUploadingDocument,
} from '../actions/documents.actions';
import {
  Document,
  DocumentMimeType,
  DocumentType,
  InProgressDocument,
  RequiredDocumentType,
} from '../models/document-type';
import { DocumentAttributeType } from '@shared/interfaces/document.interface';
import { cloneDeep } from 'lodash';

export const featureKey = 'document';

export interface DocumentState {
  activeDocument?: Document;
  allowedDocumentTypes: {
    [workspaceId: string]: DocumentType[];
  };
  loadingAllowedDocumentTypes: {
    [workspaceId: string]: boolean;
  };
  allowedDocumentTypesError: {
    [workspaceId: string]: any;
  };
  attributeTypes: DocumentAttributeType[];
  loadingAttributeTypes: boolean;
  attributeTypesError: any;
  documentTypes: {
    [entityTypeId: string]: DocumentType[];
    all: DocumentType[];
  };
  loadingDocumentTypes: {
    [entityTypeId: string]: boolean;
    all: boolean;
  };
  documentTypesError: {
    [entityTypeId: string]: any;
    all: any;
  };
  requiredDocumentTypes: {
    [workspaceTypeId: string]: RequiredDocumentType[];
  };
  loadingRequiredDocumentTypes: {
    [workspaceTypeId: string]: boolean;
  };
  requiredDocumentTypesError: {
    [workspaceTypeId: string]: any;
  };
  entityDocuments: {
    [entityTypes: string]: {
      [entityIdId: string]: Array<Document>;
    };
  };
  loadingEntityDocuments: {
    [entityType: string]: {
      [entityId: string]: boolean;
    };
  };
  entityDocumentsError: {
    [entityType: string]: {
      [entityId: string]: any;
    };
  };
  additionalEntityDocuments: {
    [entityType: string]: {
      [entityId: string]: Document[];
    };
  };
  loadingAdditionalEntityDocuments: {
    [entityType: string]: {
      [entityId: string]: boolean;
    };
  };
  additionalEntityDocumentsError: {
    [entityType: string]: {
      [entityId: string]: any;
    };
  };
  mimeTypes: DocumentMimeType[];
  documentDict: any;
  loadingMimeTypes: boolean;
  mimeTypesError: any;
  deletingDocument: {
    [documentId: string]: boolean;
  };
  deleteDocumentError: {
    [documentId: string]: any;
  };
  uploadingDocument: {
    [entityId: string]: {
      [documentId: string]: InProgressDocument;
    };
  };
  updatingDocument: {
    [documentId: string]: boolean;
  };
  updatingDocumentError: {
    [documentId: string]: any;
  };
  documentThumbnails: {
    [documentId: string]: string[];
  };
  loadingDocumentThumbnails: {
    [documentId: string]: boolean;
  };
  documentThumbnailError: {
    [documentId: string]: any;
  };
}

export const initialState: DocumentState = {
  activeDocument: undefined,
  allowedDocumentTypes: {},
  loadingAllowedDocumentTypes: {},
  allowedDocumentTypesError: {},
  documentTypes: {
    all: [],
  },
  loadingDocumentTypes: {
    all: false,
  },
  documentTypesError: {
    all: null,
  },
  requiredDocumentTypes: {},
  loadingRequiredDocumentTypes: {},
  requiredDocumentTypesError: {},
  entityDocuments: {},
  loadingEntityDocuments: {},
  entityDocumentsError: {},
  additionalEntityDocuments: {},
  loadingAdditionalEntityDocuments: {},
  additionalEntityDocumentsError: {},
  mimeTypes: [],
  loadingMimeTypes: false,
  mimeTypesError: null,
  loadingAttributeTypes: false,
  attributeTypes: [],
  attributeTypesError: null,
  deletingDocument: {},
  deleteDocumentError: {},
  uploadingDocument: {},
  updatingDocument: {},
  updatingDocumentError: {},
  documentThumbnails: {},
  loadingDocumentThumbnails: {},
  documentThumbnailError: {},
  documentDict: {},
};

export const documentReducer = createReducer(
  initialState,
  on(storeAllowedDocumentTypes, (state, { workspaceId, documentTypes }) => {
    const newAllowedDocumentTypes = {
      ...state.allowedDocumentTypes,
      [workspaceId]: documentTypes,
    };

    return {
      ...state,
      allowedDocumentTypes: newAllowedDocumentTypes,
      loadingAllowedDocumentTypes: {
        ...state.loadingAllowedDocumentTypes,
        [workspaceId]: false,
      },
      allowedDocumentTypesError: {
        ...state.allowedDocumentTypesError,
        [workspaceId]: null,
      },
    };
  }),
  on(loadingAllowedDocumentTypes, (state, { workspaceId }) => ({
    ...state,
    loadingAllowedDocumentTypes: {
      ...state.loadingAllowedDocumentTypes,
      [workspaceId]: true,
    },
    allowedDocumentTypesError: {
      ...state.allowedDocumentTypesError,
      [workspaceId]: null,
    },
  })),
  on(allowedDocumentTypesError, (state, { workspaceId, error }) => ({
    ...state,
    allowedDocumentTypesError: {
      ...state.allowedDocumentTypesError,
      [workspaceId]: error,
    },
    loadingAllowedDocumentTypes: {
      ...state.loadingAllowedDocumentTypes,
      [workspaceId]: false,
    },
  })),
  on(storeDocumentTypes, (state, { documentTypes, entityTypeId }) => {
    const newDocumentTypes = {
      ...state.documentTypes,
      [entityTypeId || 'all']: documentTypes,
    };

    return {
      ...state,
      documentTypes: newDocumentTypes,
      loadingDocumentTypes: {
        ...state.loadingDocumentTypes,
        [entityTypeId || 'all']: false,
      },
      documentTypesError: {
        ...state.documentTypesError,
        [entityTypeId || 'all']: null,
      },
    };
  }),
  on(storeDocumentDict, (state, { documentDict }) => {
    return {
      ...state,
      documentDict: documentDict,
    };
  }),

  on(loadingDocumentTypes, (state, { entityTypeId }) => ({
    ...state,
    loadingDocumentTypes: {
      ...state.loadingDocumentTypes,
      [entityTypeId || 'all']: true,
    },
    documentTypesError: {
      ...state.documentTypesError,
      [entityTypeId || 'all']: null,
    },
  })),
  on(documentTypesError, (state, { entityTypeId, error }) => ({
    ...state,
    documentTypesError: {
      ...state.documentTypesError,
      [entityTypeId || 'all']: error,
    },
    loadingDocumentTypes: {
      ...state.loadingDocumentTypes,
      [entityTypeId || 'all']: false,
    },
  })),
  on(
    storeRequiredDocumentTypes,
    (state, { documentTypes, workspaceTypeId }) => ({
      ...state,
      requiredDocumentTypes: {
        ...state.requiredDocumentTypes,
        [workspaceTypeId]: documentTypes,
      },
      requiredDocumentTypesError: {
        ...state.requiredDocumentTypesError,
        [workspaceTypeId]: null,
      },
      loadingRequiredDocumentTypes: {
        ...state.loadingRequiredDocumentTypes,
        [workspaceTypeId]: false,
      },
    }),
  ),
  on(loadingRequiredDocumentTypes, (state, { workspaceTypeId }) => ({
    ...state,
    loadingRequiredDocumentTypes: {
      ...state.loadingRequiredDocumentTypes,
      [workspaceTypeId]: true,
    },
    requiredDocumentTypesError: {
      ...state.requiredDocumentTypesError,
      [workspaceTypeId]: null,
    },
  })),
  on(requiredDocumentTypesError, (state, { workspaceTypeId, error }) => ({
    ...state,
    requiredDocumentTypesError: {
      ...state.requiredDocumentTypesError,
      [workspaceTypeId]: error,
    },
    loadingRequiredDocumentTypes: {
      ...state.loadingRequiredDocumentTypes,
      [workspaceTypeId]: false,
    },
  })),
  on(
    updateThreadtoEntityDocument,
    (state, { entityType, entityId, threadId, isResolved }) => {
      const newEntityDocuments = {
        ...state.entityDocuments,
        [entityType]: {
          ...state.entityDocuments[entityType],
          [entityId]: [
            ...state.entityDocuments[entityType][entityId].map((doc) =>
              doc.id === threadId
                ? { ...doc, messages: 1, is_resolved: isResolved }
                : doc,
            ),
          ],
        },
      };

      return {
        ...state,
        entityDocuments: newEntityDocuments,
      };
    },
  ),
  on(storeEntityDocuments, (state, { entityType, entityId, documents }) => {
    const newEntityDocuments = {
      ...state.entityDocuments,
      [entityType]: {
        ...state.entityDocuments[entityType],
        [entityId]: documents,
      },
    };

    return {
      ...state,
      entityDocuments: newEntityDocuments,
      loadingEntityDocuments: {
        ...state.loadingEntityDocuments,
        [entityType]: {
          ...state.loadingEntityDocuments[entityType],
          [entityId]: false,
        },
      },
      entityDocumentsError: {
        ...state.entityDocumentsError,
        [entityType]: {
          ...state.entityDocumentsError[entityType],
          [entityId]: null,
        },
      },
      uploadingDocument: {
        ...state.uploadingDocument,
        [entityId]: {}, // Need to empty the EntityId, because the upload is done and InProgressDocument is not needed anymore
      },
    };
  }),
  on(loadingEntityDocuments, (state, { entityType, entityId }) => ({
    ...state,
    loadingEntityDocuments: {
      ...state.loadingEntityDocuments,
      [entityType]: {
        ...state.loadingEntityDocuments[entityType],
        [entityId]: true,
      },
    },
    entityDocumentsError: {
      ...state.entityDocumentsError,
      [entityType]: {
        ...state.entityDocumentsError[entityType],
        [entityId]: null,
      },
    },
  })),
  on(entityDocumentsError, (state, { entityType, entityId, error }) => ({
    ...state,
    entityDocumentsError: {
      ...state.entityDocumentsError,
      [entityType]: {
        ...state.entityDocumentsError[entityType],
        [entityId]: error,
      },
    },
    loadingEntityDocuments: {
      ...state.loadingEntityDocuments,
      [entityType]: {
        ...state.loadingEntityDocuments[entityType],
        [entityId]: false,
      },
    },
  })),
  on(
    storeAdditionalEntityDocuments,
    (state, { entityType, entityId, documents }) => {
      const newAdditionalEntityDocuments = {
        ...state.additionalEntityDocuments,
        [entityType]: {
          ...state.additionalEntityDocuments[entityType],
          [entityId]: documents,
        },
      };

      return {
        ...state,
        additionalEntityDocuments: newAdditionalEntityDocuments,
        loadingAdditionalEntityDocuments: {
          ...state.loadingAdditionalEntityDocuments,
          [entityType]: {
            ...state.loadingAdditionalEntityDocuments[entityType],
            [entityId]: false,
          },
        },
        additionalEntityDocumentsError: {
          ...state.additionalEntityDocumentsError,
          [entityType]: {
            ...state.additionalEntityDocumentsError[entityType],
            [entityId]: null,
          },
        },
      };
    },
  ),
  on(loadingAdditionalEntityDocuments, (state, { entityType, entityId }) => ({
    ...state,
    loadingAdditionalEntityDocuments: {
      ...state.loadingAdditionalEntityDocuments,
      [entityType]: {
        ...state.loadingAdditionalEntityDocuments[entityType],
        [entityId]: true,
      },
    },
    additionalEntityDocumentsError: {
      ...state.additionalEntityDocumentsError,
      [entityType]: {
        ...state.additionalEntityDocumentsError[entityType],
        [entityId]: null,
      },
    },
  })),
  on(
    additionalEntityDocumentsError,
    (state, { entityType, entityId, error }) => ({
      ...state,
      additionalEntityDocumentsError: {
        ...state.additionalEntityDocumentsError,
        [entityType]: {
          ...state.additionalEntityDocumentsError[entityType],
          [entityId]: error,
        },
      },
      loadingAdditionalEntityDocuments: {
        ...state.loadingAdditionalEntityDocuments,
        [entityType]: {
          ...state.loadingAdditionalEntityDocuments[entityType],
          [entityId]: false,
        },
      },
    }),
  ),
  on(loadingMimeTypes, (state) => ({
    ...state,
    loadingMimeTypes: true,
    mimeTypesError: null,
  })),
  on(storeMimeTypes, (state, { mimeTypes }) => ({
    ...state,
    mimeTypes,
    loadingMimeTypes: false,
    mimeTypesError: null,
  })),
  on(mimeTypesError, (state, { error }) => ({
    ...state,
    mimeTypesError: error,
    loadingMimeTypes: false,
  })),
  on(deletingDocument, (state, { documentId }) => ({
    ...state,
    deletingDocument: {
      ...state.deletingDocument,
      [documentId]: true,
    },
    deleteDocumentError: {
      ...state.deleteDocumentError,
      [documentId]: null,
    },
  })),
  on(deleteDocumentError, (state, { documentId, error }) => ({
    ...state,
    deleteDocumentError: {
      ...state.deleteDocumentError,
      [documentId]: error,
    },
    deletingDocument: {
      ...state.deletingDocument,
      [documentId]: false,
    },
  })),
  on(
    deleteDocumentSuccess,
    (state, { documentId, entityId, entityType, documentIds }) => {
      const nextState = cloneDeep(state);

      if (documentIds) {
        if (nextState.entityDocuments[entityType]) {
          nextState.entityDocuments[entityType][entityId] =
            nextState.entityDocuments[entityType][entityId].filter(
              (doc) => !documentIds.includes(doc.id),
            );
        }

        Object.keys(nextState.uploadingDocument).forEach((entityId) => {
          nextState.uploadingDocument[entityId] = Object.fromEntries(
            Object.entries(nextState.uploadingDocument[entityId]).filter(
              ([_, document]) => !documentIds.includes(document.id),
            ),
          );
        });

        documentIds.forEach((documentId) => {
          delete nextState.deletingDocument[documentId];
          delete nextState.deleteDocumentError[documentId];
        });
      } else if (documentId) {
        if (nextState.entityDocuments[entityType]) {
          nextState.entityDocuments[entityType][entityId] =
            nextState.entityDocuments[entityType][entityId].filter(
              (doc) => doc.id !== documentId,
            );
        }

        Object.keys(nextState.uploadingDocument).forEach((entityId) => {
          nextState.uploadingDocument[entityId] = Object.fromEntries(
            Object.entries(nextState.uploadingDocument[entityId]).filter(
              ([_, document]) => document.document?.id !== documentId,
            ),
          );
        });

        nextState.deleteDocumentError[documentId] = null;
        nextState.deletingDocument[documentId] = false;
      }

      return nextState;
    },
  ),
  on(
    uploadingDocument,
    (
      state,
      {
        uuid,
        entityId,
        filename,
        additional,
        documentTypeId,
        documentTypeName,
        documentName,
        questionId,
      },
    ) => ({
      ...state,
      uploadingDocument: {
        ...state.uploadingDocument,
        [entityId]: {
          ...state.uploadingDocument[entityId],
          [uuid]: {
            id: uuid,
            uploading: true,
            progress: 0,
            name: filename,
            error: null,
            is_additional: additional,
            documentTypeId,
            documentTypeName,
            documentName,
            questionId,
          },
        },
      },
    }),
  ),
  on(uploadDocumentError, (state, { uuid, error, entityId }) => ({
    ...state,
    uploadingDocument: {
      ...state.uploadingDocument,
      [entityId]: {
        ...state.uploadingDocument[entityId],
        [uuid]: {
          ...state.uploadingDocument[entityId][uuid],
          uploading: false,
          error,
        },
      },
    },
  })),
  on(uploadDocumentProgress, (state, { uuid, progress, entityId }) => ({
    ...state,
    uploadingDocument: {
      ...state.uploadingDocument,
      [entityId]: {
        ...state.uploadingDocument?.[entityId],
        [uuid]: {
          ...state.uploadingDocument?.[entityId]?.[uuid],
          progress,
        },
      },
    },
  })),
  on(cancelDocumentUpload, (state, { uuid, entityId }) => {
    const newUploadingDocument = { ...state.uploadingDocument[entityId] };
    delete newUploadingDocument[uuid];

    return {
      ...state,
      uploadingDocument: {
        ...state.uploadingDocument,
        [entityId]: newUploadingDocument,
      },
    };
  }),
  on(uploadDocumentSuccess, (state, { uuid, entityId, document }) => {
    const newUploadingDocument = { ...state.uploadingDocument[entityId] };

    newUploadingDocument[uuid] = {
      ...newUploadingDocument[uuid],
      uploading: false,
      document,
    };

    return {
      ...state,
      uploadingDocument: {
        ...state.uploadingDocument,
        [entityId]: newUploadingDocument,
      },
    };
  }),
  on(updatingDocument, (state, { documentId }) => ({
    ...state,
    updatingDocument: {
      ...state.updatingDocument,
      [documentId]: true,
    },
    updatingDocumentError: {
      ...state.updatingDocumentError,
      [documentId]: null,
    },
  })),
  on(updateDocumentError, (state, { documentId, error }) => ({
    ...state,
    updatingDocumentError: {
      ...state.updatingDocumentError,
      [documentId]: error,
    },
    updatingDocument: {
      ...state.updatingDocument,
      [documentId]: false,
    },
  })),
  on(updateDocumentSuccess, (state, { documentId, document }) => {
    const nextState = cloneDeep(state);

    if (document) {
      if (nextState.activeDocument?.id === documentId) {
        nextState.activeDocument = document;
      }

      const entityEntry =
        nextState.entityDocuments[document.entity_type]?.[document.entity_id];

      if (entityEntry) {
        const index = entityEntry.findIndex((doc) => doc.id === document.id);

        if (index !== -1) {
          entityEntry[index] = document;
        }

        nextState.entityDocuments[document.entity_type][document.entity_id] =
          entityEntry;
      }
    }

    nextState.updatingDocumentError[documentId] = null;
    nextState.updatingDocument[documentId] = false;

    return nextState;
  }),
  on(storeDocumentThumbnails, (state, { documentId, thumbnails }) => ({
    ...state,
    documentThumbnails: {
      ...state.documentThumbnails,
      [documentId]: thumbnails,
    },
    loadingDocumentThumbnails: {
      ...state.loadingDocumentThumbnails,
      [documentId]: false,
    },
    documentThumbnailError: {
      ...state.documentThumbnailError,
      [documentId]: null,
    },
  })),
  on(loadingDocumentThumbnails, (state, { documentId }) => ({
    ...state,
    loadingDocumentThumbnails: {
      ...state.loadingDocumentThumbnails,
      [documentId]: true,
    },
    documentThumbnailError: {
      ...state.documentThumbnailError,
      [documentId]: null,
    },
  })),
  on(documentThumbnailsError, (state, { documentId, error }) => ({
    ...state,
    documentThumbnailError: {
      ...state.documentThumbnailError,
      [documentId]: error,
    },
    loadingDocumentThumbnails: {
      ...state.loadingDocumentThumbnails,
      [documentId]: false,
    },
  })),
  on(storeActiveDocument, (state, { document }) => ({
    ...state,
    activeDocument: document,
  })),
  on(loadingAttributeTypes, (state) => ({
    ...state,
    loadingAttributeTypes: true,
    attributeTypesError: null,
  })),
  on(storeAttributeTypes, (state, { attributeTypes }) => ({
    ...state,
    attributeTypes,
    loadingAttributeTypes: false,
    attributeTypesError: null,
  })),
  on(attributeTypesError, (state, { error }) => ({
    ...state,
    attributeTypesError: error,
    loadingAttributeTypes: false,
  })),
  on(storeDocument, (state, { document, workspaceId }) => {
    if (!state.entityDocuments)
      return {
        ...state,
      };
    //should exit if no workspace documents
    if (!state.entityDocuments.workspace)
      return {
        ...state,
      };
    const currentWorkspace = state.entityDocuments.workspace[workspaceId] || [];

    if (currentWorkspace.some((doc) => doc.id === document.id)) {
      return state;
    } else if (
      Object.values(state.uploadingDocument[workspaceId] || {}).some(
        (inProgressDocument) => inProgressDocument.name === document.title,
      )
    ) {
      return state;
    }
    const updatedWorkspace = [...currentWorkspace, document];

    return {
      ...state,
      entityDocuments: {
        ...state.entityDocuments,
        workspace: {
          ...state.entityDocuments.workspace,
          [workspaceId]: updatedWorkspace,
        },
      },
    };
  }),
  on(clearUploadingDocuments, (state) => ({
    ...state,
    uploadingDocument: {},
  })),
  on(removeDocuments, (state, { entityType, entityId, documentIds }) => {
    const nextState = cloneDeep(state);

    if (nextState.entityDocuments[entityType]) {
      nextState.entityDocuments[entityType][entityId] =
        nextState.entityDocuments[entityType][entityId].filter(
          (doc) => !documentIds.includes(doc.id),
        );
    }

    Object.keys(nextState.uploadingDocument).forEach((entityId) => {
      nextState.uploadingDocument[entityId] = Object.fromEntries(
        Object.entries(nextState.uploadingDocument[entityId]).filter(
          ([_, document]) => {
            if (document.document) {
              return !documentIds.includes(document.document?.id ?? '');
            }
            return !documentIds.includes(document.id);
          },
        ),
      );
    });

    documentIds.forEach((documentId) => {
      delete nextState.deletingDocument[documentId];
      delete nextState.deleteDocumentError[documentId];
    });

    return nextState;
  }),
  on(removeAllDocuments, (state, { entityType, entityId }) => {
    const nextState = cloneDeep(state);

    if (nextState.entityDocuments[entityType]) {
      if (entityId) {
        nextState.entityDocuments[entityType][entityId] = [];
      } else {
        delete nextState.entityDocuments[entityType];
      }
    }

    Object.keys(nextState.uploadingDocument).forEach((id) => {
      if (entityId) {
        if (id === entityId) {
          nextState.uploadingDocument[entityId] = {};
        }
      } else {
        delete nextState.uploadingDocument[id];
      }
    });

    // Clear all deleting and error documents
    nextState.deletingDocument = {};
    nextState.deleteDocumentError = {};

    return nextState;
  }),
  on(clearSingleUploadingDocument, (state, { uuid, entityId }) => {
    const entityUploads = { ...state.uploadingDocument[entityId] };
    delete entityUploads[uuid];

    return {
      ...state,
      uploadingDocument: {
        ...state.uploadingDocument,
        [entityId]: entityUploads,
      },
    };
  }),
);
